import { type ComputedRef, type Ref, computed, onBeforeUnmount, ref, watch } from 'vue';
import type { JobApplicantStatusHistory } from '@factoryfixinc/ats-interfaces';
import MeService from '@/core/shared/me/me.service';
import ConversationMessageService from '@/core/conversations/message/conversation-message.service';
import ConversationIndexService from '@/core/conversations/conversation-index/conversation-index.service';
import ProProfileService from '@/core/conversations/pro-profile/pro-profile.service';
import ProjectService from '@/core/shared/project/project.service';
import ProjectCopilotConfigurationUtils from '@/utils/project/project-copilot-configuration.utils';
import { ErrorService } from '@/core/shared/errors/error.service';
import { SnackbarService } from '@/core/shared/snackbar/snackbar.service';
import { USER_OPTED_OUT_ERROR_MESSAGE } from '@/core/sourcing/messaging/shared/constants/message-constants';
import ScrollUtils from '@/utils/scroll-utils';
import type { ConversationMessage } from '@/core/conversations/message/conversation-message.type';
import type { JobApplicantWithJobTitle } from '@/core/shared/job/job.type';

const MESSAGE_POLL_INTERVAL_MS = 5000;

interface UseConversationThread {
  messagesViewElementRef: Ref<HTMLElement | undefined>;
  messageList: ComputedRef<ConversationMessage[]>;
  doNotContactHistoryUnavailable: ComputedRef<boolean>;
  jobApplicant: ComputedRef<JobApplicantWithJobTitle | undefined>;
  statusHistory: ComputedRef<JobApplicantStatusHistory[]>;
  isMessageListVisible: Ref<boolean>;
  userProfileId: ComputedRef<number | undefined>;
  currentProId: ComputedRef<number | undefined>;
  aiRecruiterName: ComputedRef<string>;
  handleScroll: (event: UIEvent) => Promise<void>;
  sendMessage: (messageContent: string) => Promise<void>;
}

/**
 * Mobile-only conversation thread orchestration. Duplicates the desktop load,
 * pagination, polling, and scroll flow without changing conversation-tab.vue.
 *
 * Load order is intentional: profile selects the application before status
 * history and messages, so score/status cards have the selected application.
 */
export function useConversationThread(): UseConversationThread {
  const meService = new MeService();
  const messageService = new ConversationMessageService();
  const conversationIndexService = new ConversationIndexService();
  const proProfileService = new ProProfileService();
  const projectService = new ProjectService();

  const messagesViewElementRef = ref<HTMLElement>();
  const nextPageToFetch = ref(1);
  const isMessageListVisible = ref(true);
  const activeConversationLoadVersion = ref(0);
  const fetchingMoreRequest = ref<{ conversationId: number; loadVersion: number }>();

  const conversationId = computed(
    () => conversationIndexService.selectedConversationIndex?.conversationId,
  );
  const currentProId = computed(() => conversationIndexService.selectedConversationIndex?.proId);
  const jobApplicant = computed(() => proProfileService.selectedProApplication);
  const statusHistory = computed(() => proProfileService.selectedProJobApplicantStatusHistory);
  const messageList = computed(() => messageService.messageList);
  const doNotContactHistoryUnavailable = computed(
    () => messageService.doNotContactHistoryUnavailable,
  );
  const userProfileId = computed(() => meService.userProfile?.id);

  // MessageList needs the Copilot display name for bot/score turns. getLocalProjectByJobId
  // is a synchronous lookup over the reactive project store, so a computed resolves the
  // name and re-runs when the job or the loaded projects change. Exposed here so consumers
  // (e.g. MobileConversation) feed MessageList without re-deriving it.
  const aiRecruiterName = computed(() => {
    const jobId = jobApplicant.value?.jobId;
    const currentProject = jobId ? projectService.getLocalProjectByJobId(jobId) : null;
    return ProjectCopilotConfigurationUtils.getCopilotName(currentProject);
  });

  async function fetchInitialMessages(id: number): Promise<void> {
    nextPageToFetch.value = 1;
    await messageService.fetchConversationMessages(id, { page: 1, clearMessages: true });
    nextPageToFetch.value++;
  }

  async function fetchExistingMessages(id: number): Promise<void> {
    await messageService.fetchConversationMessages(id, { page: 1 });
  }

  function isActiveConversationRequest(id: number, loadVersion: number): boolean {
    return id === conversationId.value && loadVersion === activeConversationLoadVersion.value;
  }

  async function fetchMoreMessages(id: number): Promise<void> {
    const loadVersion = activeConversationLoadVersion.value;
    const isFetchingCurrentPage =
      fetchingMoreRequest.value?.conversationId === id &&
      fetchingMoreRequest.value.loadVersion === loadVersion;

    if (!isActiveConversationRequest(id, loadVersion) || isFetchingCurrentPage) {
      return;
    }

    fetchingMoreRequest.value = { conversationId: id, loadVersion };
    try {
      const newMessages = await messageService.fetchConversationMessages(id, {
        page: nextPageToFetch.value,
      });

      if (!isActiveConversationRequest(id, loadVersion)) {
        return;
      }

      if (newMessages.length > 0) {
        nextPageToFetch.value++;
      }
    } finally {
      if (
        fetchingMoreRequest.value?.conversationId === id &&
        fetchingMoreRequest.value.loadVersion === loadVersion
      ) {
        fetchingMoreRequest.value = undefined;
      }
    }
  }

  async function handleScroll(event: UIEvent): Promise<void> {
    if (conversationId.value && ScrollUtils.isStartOfScroll(event.target)) {
      await fetchMoreMessages(conversationId.value);
    }
  }

  async function sendMessage(messageContent: string): Promise<void> {
    if (!conversationId.value) {
      return;
    }

    try {
      await messageService.sendMessage(
        conversationId.value,
        messageContent,
        jobApplicant.value?.jobId,
      );
      messageService.scrollMessagesToBottom = true;
    } catch (error) {
      // The opted-out case already shows its own snackbar in the message service, so
      // only surface other failures here. Rethrow so the composer keeps the typed
      // text for a retry instead of clearing it.
      const isOptedOut = error instanceof Error && error.message === USER_OPTED_OUT_ERROR_MESSAGE;
      if (!isOptedOut) {
        ErrorService.captureException(error);
        SnackbarService.critical('Could not send the message.');
      }
      throw error;
    }
  }

  let initialScrollTimeoutId: number | undefined;

  function clearPollingInterval(): void {
    if (messageService.messagesPollingIntervalId) {
      window.clearInterval(messageService.messagesPollingIntervalId);
    }
  }

  function clearInitialScrollTimeout(): void {
    if (initialScrollTimeoutId !== undefined) {
      window.clearTimeout(initialScrollTimeoutId);
      initialScrollTimeoutId = undefined;
    }
  }

  function setPollingInterval(id: number): void {
    clearPollingInterval();

    messageService.messagesPollingIntervalId = window.setInterval(async () => {
      await fetchExistingMessages(id);

      if (ScrollUtils.isEndOfScroll(messagesViewElementRef.value)) {
        messageService.scrollMessagesToBottom = true;
      }
    }, MESSAGE_POLL_INTERVAL_MS);
  }

  watch(
    conversationId,
    async (newConversationId, oldConversationId) => {
      if (newConversationId === oldConversationId) {
        return;
      }

      if (!newConversationId) {
        activeConversationLoadVersion.value++;
        fetchingMoreRequest.value = undefined;
        clearPollingInterval();
        clearInitialScrollTimeout();
        return;
      }

      const loadVersion = ++activeConversationLoadVersion.value;
      fetchingMoreRequest.value = undefined;

      try {
        isMessageListVisible.value = false;
        clearPollingInterval();

        const employerId = meService.employer?.id;
        if (employerId && currentProId.value) {
          await proProfileService.getProProfile(employerId, currentProId.value);
          await proProfileService.getApplicantStatusHistory();
        }

        // The awaits above can outlive a fast conversation switch. The message store
        // is shared, so a stale run would wipe the now-current thread and point the
        // poll at the wrong conversation. Bail once this is no longer the active load.
        if (!isActiveConversationRequest(newConversationId, loadVersion)) {
          return;
        }

        await fetchInitialMessages(newConversationId);

        if (!isActiveConversationRequest(newConversationId, loadVersion)) {
          return;
        }

        // Defer a tick so a long thread reliably lands at the bottom.
        clearInitialScrollTimeout();
        initialScrollTimeoutId = window.setTimeout(() => {
          initialScrollTimeoutId = undefined;
          ScrollUtils.scrollToBottom(messagesViewElementRef.value, 'instant');
        }, 100);

        setPollingInterval(newConversationId);
      } catch (error) {
        ErrorService.captureException(error);
        SnackbarService.critical('Could not load the conversation.');
      } finally {
        // Only the still-active run owns the visibility flag; a stale run flipping it
        // back on would reveal the wrong thread mid-switch.
        if (isActiveConversationRequest(newConversationId, loadVersion)) {
          isMessageListVisible.value = true;
        }
      }
    },
    { immediate: true },
  );

  watch(
    () => messageService.scrollMessagesToBottom,
    (shouldScroll) => {
      if (shouldScroll) {
        ScrollUtils.scrollToBottom(messagesViewElementRef.value, 'smooth');
        messageService.scrollMessagesToBottom = false;
      }
    },
  );

  onBeforeUnmount(() => {
    clearPollingInterval();
    clearInitialScrollTimeout();
  });

  return {
    messagesViewElementRef,
    messageList,
    doNotContactHistoryUnavailable,
    jobApplicant,
    statusHistory,
    isMessageListVisible,
    userProfileId,
    currentProId,
    aiRecruiterName,
    handleScroll,
    sendMessage,
  };
}
